import React from "react";
//#region types
import { Customer, User } from "models/propTypes/userProfilePropTypes";
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { Amount } from "models/utilTypes/currencyType";
import { InvoiceFormTypes } from "models/formTypes";
//#endregion
//#region components
import { TableAsync, useFilterContext } from "components/common";
import { RecurringInvoiceSendType, RecurringInvoiceStatusTags } from "components/invoice";
import { useNavigate, useLocation } from "react-router-dom";
import { useRecurringInvoiceService } from "serviceHooks";
//#endregion
import { UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const RecurringInvoiceListView: React.FC = (props) => {
  const recurringInvoiceService = useRecurringInvoiceService();
  const location = useLocation();
  const navigate = useNavigate();

  const { filter } = useFilterContext<InvoiceFormTypes.RecurringInvoiceFilter>();

  const filterFunc = React.useMemo(
    () => (baseFilter: WintUtilsWebApi.Filter) => recurringInvoiceService.reportFilter(baseFilter, filter),
    [filter]
  );

  const onRowClick = (row: WintSuperkollAPIModel.Invoice) => {
    navigate(UrlHelper.formatPath(`/RecurringInvoice/${row.id}`), {
      state: { background: location, preserveSearch: true },
    });
  };

  return (
    <TableAsync<WintSuperkollAPIModel.RecurringInvoice>
      properties={{
        ownerName: {
          description: TranslationKeys.Common.Owner,
          helpText: TranslationKeys.Invoice.RecurringListOwnerDescription,
          formatter: (values: WintSuperkollAPIModel.RecurringInvoice) =>
            new User(values.ownerId, values.ownerName, true),
          alignment: "left",
          width: 40,
        },
        recurringDescription: {
          description: TranslationKeys.Common.Description,
          noWrap: true,
          maxWidth: 125,
        },
        firstPostingDate: {
          description: TranslationKeys.Invoice.FirstInvoice,
          helpText: TranslationKeys.Invoice.RecurringListFirstInvoiceDescription,
          formatter: (values: WintSuperkollAPIModel.RecurringInvoice) =>
            values.firstPostingDate && new Date(values.firstPostingDate),
          noWrap: true,
          width: 80,
        },
        stopDate: {
          description: TranslationKeys.Invoice.LastInvoice,
          helpText: TranslationKeys.Invoice.RecurringListLastInvoiceDescription,
          formatter: (values: WintSuperkollAPIModel.RecurringInvoice) => values.stopDate && new Date(values.stopDate),
          noWrap: true,
          width: 80,
        },
        customerName: {
          description: TranslationKeys.Invoice.Customer,
          helpText: TranslationKeys.Invoice.RecurringListCustomerDescription,
          formatter: (values: WintSuperkollAPIModel.RecurringInvoice) =>
            new Customer(values.customerId, values.customerName),
          noWrap: true,
          maxWidth: 120,
        },
        recurringInvoiceSendType: {
          description: TranslationKeys.Common.Sent,
          helpText: TranslationKeys.Invoice.RecurringListSentDescription,
          formatter: (values: WintSuperkollAPIModel.RecurringInvoice) => (
            <RecurringInvoiceSendType type={values.recurringInvoiceSendType} />
          ),
          width: 80,
        },
        numberOfSentInvoices: {
          description: TranslationKeys.Invoice.NumOfInvoices,
          helpText: TranslationKeys.Invoice.RecurringListNumOfInvoicesDescription,
          width: 40,
        },
        status: {
          description: TranslationKeys.Common.Status,
          helpText: TranslationKeys.Invoice.RecurringListStatusDescription,
          formatter: (values: WintSuperkollAPIModel.RecurringInvoice) => (
            <RecurringInvoiceStatusTags status={values.status} />
          ),
          width: 60,
        },
        totalAmount: {
          description: TranslationKeys.Common.Amount,
          helpText: TranslationKeys.Invoice.RecurringListAmountDescription,
          formatter: (values: WintSuperkollAPIModel.RecurringInvoice) =>
            new Amount(values.currency, values.totalAmount),
          alignment: "right",
          noWrap: true,
        },
        revenueFromPastInvoices: {
          description: TranslationKeys.Common.TotalRevenue,
          helpText: TranslationKeys.Invoice.RecurringListIncomeDescription,
          formatter: (values: WintSuperkollAPIModel.RecurringInvoice) =>
            new Amount(values.currency, values.revenueFromPastInvoices),
          alignment: "right",
          noWrap: true,
          className: cssExports.textBlack,
          classification: "amount",
        },
      }}
      orderByProperty="firstPostingDate"
      orderByDescending
      filter={filterFunc}
      pagination="advanced"
      maxRowCount={10}
      rowClickHandler={onRowClick}
    />
  );
};
export default RecurringInvoiceListView;
